import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMapMarkerAlt, faPhone } from '@fortawesome/free-solid-svg-icons'
import GPMapSelection from '../GPMapSelection'
import GpAddressForm from '../GpAddressForm'
import useAxiosPrivate from '../../hooks/useAxiosPrivate'
import useAuth from '../../hooks/useAuth'
import { toast } from 'sonner'

const GPDetailsSection = ({
  selectedGP,
  setSelectedGP,
  showGpMap,
  setShowGpMap,
  enterDetailsManually,
  setEnterDetailsManually,
  manualDetails,
  setManualDetails,
  isNotRegisteredWithGP,
  setIsNotRegisteredWithGP,
  handleClearSelectedGP,
  handleEditSelectedGP,
  showTitle = true,
  profileData = null,
  userInfo = null,
  setUserInfo = null,
  dependant = false, // new prop
  alterText = false
}) => {
  const axios = useAxiosPrivate()
  const { auth, setAuth } = useAuth()

  const updateProfileImmediately = async (gpDetails) => {
    try {
      if (!setUserInfo || !userInfo || !auth?.user?.id) {
        console.error('Profile update skipped - missing required data or context')
        return
      }

      const updatedUserInfo = {
        ...userInfo,
        gp: gpDetails,
      }
      setUserInfo(updatedUserInfo)

      const data = {
        data: profileData || {
          first_name: auth.user.name?.split(' ')[0] || '',
          last_name: auth.user.name?.split(' ')[1] || '',
          email: auth.user.email || '',
          phone_number: auth.user.phone_number || '',
          date_of_birth: auth.user.date_of_birth || {},
        },
        info: updatedUserInfo,
        user_id: auth?.user?.id,
      }

      const response = await axios.post('/profile/update', data)

      if (!response.data.error) {
        setAuth((prev) => ({
          ...prev,
          user: {
            ...prev.user,
            user_details: {
              ...prev.user.user_details,
              gp: gpDetails,
            },
          },
        }))

        toast.success('GP details updated successfully!')
      }
    } catch (error) {
      console.error('Error updating GP details:', error)
      toast.error('Failed to update GP details. Please try again.')
    }
  }

  return (
    <>
      {showTitle && (
        <div className="sm:col-span-6">
          <h2 className="text-xl font-medium">GP Details</h2>
        </div>
      )}

      <div className="sm:col-span-6">
        <section>
          {showGpMap ? (
            <GPMapSelection
              onHideMap={() => setShowGpMap(false)}
              selectedGP={selectedGP}
              setSelectedGP={setSelectedGP}
              onChoose={async (gpDetails) => {
                setSelectedGP(gpDetails)
                setIsNotRegisteredWithGP(false)
                setEnterDetailsManually(false)
                setShowGpMap(false)
                await updateProfileImmediately(gpDetails)
              }}
            />
          ) : (
            <div>
              {selectedGP ? (
                <div className="bg-white p-4 rounded-md shadow-md border border-gray-300 relative">
                  <h3 className="font-semibold text-portal-dark-blue mb-2">
                    {selectedGP.name || selectedGP.surgeryName || 'GP Surgery'}
                  </h3>

                  <div className="mb-4 flex items-start text-gray-500">
                    <FontAwesomeIcon
                      icon={faMapMarkerAlt}
                      className="text-gray-500 mt-1 mr-2"
                    />
                    <div>
                      {selectedGP.address && (
                        <p>
                          {[
                            selectedGP.address.addressLine1,
                            selectedGP.address.addressLine2,
                            selectedGP.address.city,
                            selectedGP.address.postcode,
                            selectedGP.address.country,
                          ]
                            .filter(Boolean) // removes empty values
                            .join(', ')}
                        </p>
                      )}
                      {selectedGP.postcode && <p>{selectedGP.postcode}</p>}
                    </div>
                  </div>

                  {selectedGP.phone && (
                    <p className="mt-4 flex items-center gap-2 text-sm text-gray-500">
                      <FontAwesomeIcon icon={faPhone} />
                      {selectedGP.phone}
                    </p>
                  )}

                  <div className="absolute bottom-3 right-3 flex gap-4 text-sm text-blue-600 cursor-pointer select-none">
                    <span
                      onClick={handleClearSelectedGP}
                      className="hover:underline"
                    >
                      Clear
                    </span>
                    <span
                      onClick={handleEditSelectedGP}
                      className="hover:underline"
                    >
                      Edit
                    </span>
                  </div>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      if (!isNotRegisteredWithGP) {
                        setShowGpMap(true)
                      }
                    }}
                    disabled={isNotRegisteredWithGP}
                    className={`w-56 h-12 px-6 py-3 bg-hub-primary rounded-md text-sm font-bold hover:bg-hub-primary-hover text-white flex justify-center items-center ${isNotRegisteredWithGP
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : ''
                      }`}
                  >
                    <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2" />{' '}
                    Choose on map
                  </button>

                  {!enterDetailsManually && !isNotRegisteredWithGP && (
                    <div className="mt-2 mb-2">
                      <button
                        type="button"
                        className="underline"
                        onClick={() => setEnterDetailsManually(true)}
                      >
                        Enter Details Manually
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {enterDetailsManually && (
            <GpAddressForm
              manualDetails={manualDetails}
              setManualDetails={setManualDetails}
            />
          )}

          <div className="mt-2">
            <label className="text-sm flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isNotRegisteredWithGP}
                onChange={() => {
                  setIsNotRegisteredWithGP(!isNotRegisteredWithGP)
                  setEnterDetailsManually(false)
                  setShowGpMap(false)
                  setSelectedGP(null)
                }}
              />
              {dependant
                ? `My dependant is not registered with a GP surgery${alterText ? ' / do not wish to disclose at this time' : ''}`
                : `I am not registered with a GP surgery${alterText ? ' / do not wish to disclose at this time' : ''}`}
            </label>
          </div>
        </section>
      </div>
    </>
  )
}

export default GPDetailsSection
